import { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Eraser, RotateCcw, Check, PenTool } from 'lucide-react';

interface SignatureCaptureProps {
  onSignatureChange: (signature: string | null) => void;
  existingSignature?: string | null;
}

export function SignatureCapture({ onSignatureChange, existingSignature }: SignatureCaptureProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [penWidth, setPenWidth] = useState(3);
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);
  const [canvasHeight, setCanvasHeight] = useState(300);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size responsively
    const updateCanvasSize = () => {
      const rect = canvas.getBoundingClientRect();
      const isLandscape = window.innerWidth > window.innerHeight;
      const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1024;

      // Adjust canvas height based on orientation
      if (isTablet && isLandscape) {
        setCanvasHeight(250); // Smaller height in landscape
      } else {
        setCanvasHeight(300); // Default height
      }

      canvas.width = rect.width;
      canvas.height = rect.height;

      // Re-apply canvas settings
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = penWidth;
        setContext(ctx);

        // Redraw existing signature if any
        if (existingSignature) {
          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            setHasSignature(true);
          };
          img.src = existingSignature;
        }
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    // Prevent iOS bounce/scroll when drawing
    const preventBounce = (e: TouchEvent) => {
      if (e.target === canvas) {
        e.preventDefault();
      }
    };
    
    document.addEventListener('touchmove', preventBounce, { passive: false });

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      document.removeEventListener('touchmove', preventBounce);
    };
  }, [existingSignature, penWidth]);

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    if (!context || !canvasRef.current) return;

    // Prevent default touch behaviors that might cause navigation
    e.preventDefault();
    e.stopPropagation();
    
    // Additional prevention for touch events
    if ('touches' in e && e.nativeEvent) {
      e.nativeEvent.preventDefault();
      e.nativeEvent.stopImmediatePropagation();
    }

    setIsDrawing(true);
    setHasSignature(true);

    const rect = canvasRef.current.getBoundingClientRect();
    let x, y;

    if ('touches' in e) {
      // For touch events, prevent scrolling and zooming
      if (e.touches.length > 1) return; // Ignore multi-touch
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || !context || !canvasRef.current) return;

    // Prevent default touch behaviors
    e.preventDefault();
    e.stopPropagation();

    const rect = canvasRef.current.getBoundingClientRect();
    let x, y;

    if ('touches' in e) {
      // For touch events, prevent scrolling and zooming
      if (e.touches.length > 1) return; // Ignore multi-touch
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = (e?: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;

    // Prevent event propagation if event exists
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setIsDrawing(false);

    // Save signature as base64
    if (canvasRef.current && hasSignature) {
      const signature = canvasRef.current.toDataURL('image/png');
      onSignatureChange(signature);
    }
  };

  const clearSignature = () => {
    if (!context || !canvasRef.current) return;

    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasSignature(false);
    onSignatureChange(null);
  };

  const undoLastStroke = () => {
    // This is a simplified undo - just clears the canvas
    // For a proper undo, you'd need to store drawing history
    clearSignature();
  };

  return (
    <div className="space-y-4" style={{ touchAction: 'pan-y' }}>
      <Card className="p-4" style={{ touchAction: 'auto' }}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PenTool className="h-4 w-4 text-gray-600" />
            <span className="text-sm font-medium">Sign in the box below</span>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={undoLastStroke}
              disabled={!hasSignature}
              className="h-8 px-2"
            >
              <RotateCcw className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline">Undo</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearSignature}
              disabled={!hasSignature}
              className="h-8 px-2"
            >
              <Eraser className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline">Clear</span>
            </Button>
          </div>
        </div>

        {/* Pen width selector for better tablet experience */}
        <div className="mb-3 flex items-center gap-3">
          <span className="text-sm text-gray-600">Pen size:</span>
          <div className="flex gap-2">
            {[2, 3, 4, 5].map((width) => (
              <button
                key={width}
                type="button"
                onClick={() => {
                  setPenWidth(width);
                  if (context) {
                    context.lineWidth = width;
                  }
                }}
                className={`h-8 w-8 rounded-full border-2 flex items-center justify-center ${
                  penWidth === width ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                }`}
              >
                <div
                  className="rounded-full bg-black"
                  style={{
                    width: `${width * 2}px`,
                    height: `${width * 2}px`,
                  }}
                />
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <canvas
            ref={canvasRef}
            className="w-full border-2 border-dashed border-gray-300 rounded-lg bg-white cursor-crosshair"
            style={{ 
              height: `${canvasHeight}px`,
              touchAction: 'none', // Prevent scrolling and pinch-zoom on touch devices
              WebkitUserSelect: 'none', // Prevent text selection
              userSelect: 'none',
              WebkitTouchCallout: 'none' // Prevent callout on long-press
            }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            onTouchCancel={stopDrawing}
            onContextMenu={(e) => e.preventDefault()} // Prevent right-click menu
          />

          {/* Signature guide lines */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute bottom-16 left-4 right-4 border-b border-gray-200" />
            <div className="absolute bottom-16 left-4 text-xs text-gray-400">Sign here</div>
          </div>

          {hasSignature && (
            <div className="absolute top-2 right-2">
              <Badge variant="success" className="flex items-center gap-1">
                <Check className="h-3 w-3" />
                Signed
              </Badge>
            </div>
          )}
        </div>

        <p className="mt-2 text-xs text-gray-500">
          Use your finger or stylus to sign above. The signature will be saved automatically.
        </p>
      </Card>
    </div>
  );
}

// Badge component for the signature status
function Badge({
  children,
  variant,
  className,
}: {
  children: React.ReactNode;
  variant: string;
  className?: string;
}) {
  const variantClasses = {
    success: 'bg-green-100 text-green-800',
    secondary: 'bg-gray-100 text-gray-800',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${variantClasses[variant as keyof typeof variantClasses]} ${className}`}
    >
      {children}
    </span>
  );
}
